/**
 * Converts an enum value to its label.
 *
 * Example:
 * An enum looks like this:
 * ```typescript
 * enum MyEnum {
 *  FIRST = 1,
 *  SECOND = 2,
 * }
 * ```
 *
 * During runtime, the enum looks like this:
 * ```typescript
 * {
 *   "FIRST": 1,
 *   "SECOND": 2,
 *   "1": "FIRST",
 *   "2": "SECOND",
 * }
 * ```
 *
 * To convert the value `MyEnum.FIRST = 1` to its label, we have to look up the key in the enum object.
 * Note that during runtime, the mapping between enum value and key is reversed and it is a string.
 *
 * Using this function, we can convert the value `1` to the label `FIRST`.
 *
 * @param val The enum value to convert to a label.
 * @param enumMap The enum object.
 *
 * @returns The label of the enum value.
 */
export default <
  T extends {
    [key: string]: string | number;
  },
  K extends T[keyof T],
>(
  enumMap: T,
  val: K,
) => {
  const key = Object.keys(enumMap)
    // note the string conversion of the value
    .find((k) => k === String(val));

  return (enumMap[key] as string)?.replace(/_/g, ' ') ?? '';
};
